import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { Button } from '@scio3/ui';
import { usePermissions } from '@/hooks/usePermissions';
import { useOptibotStore } from '../stores/optibot-store';
import { DeviceTroubleshootSessionView } from './DeviceTroubleshootSessionView';
import {
  useDeviceTroubleshootSessions,
  useDeviceTroubleshootTranscript,
  useResumeDeviceTroubleshootSession,
} from '../hooks/useDeviceTroubleshootHistory';

/** The roomy surface for a single session, shown in place of the chat when one is focused. Live sessions
 *  open the SSE + show the steer composer; history sessions replay the recorded transcript (read-only).
 *  The chat thread is preserved underneath — "Back to chat" just clears the focus. */
export function FocusedSessionPane() {
  const { t } = useTranslation();
  const focused = useOptibotStore((s) => s.focusedSession);
  const closeSession = useOptibotStore((s) => s.closeSession);
  const openSession = useOptibotStore((s) => s.openSession);
  const { isTeamAdmin, isRootOwner } = usePermissions();
  const canResume = isTeamAdmin || isRootOwner; // re-attach re-mints a live token → write-capable roles only

  // Re-attach a STILL-LIVE session that was opened as history. The OptiBot history list always opens history
  // (no token), and an in-chat card falls back to history after a reload once its in-memory conn is gone — so
  // a session that's actually still running would render as a stale "Ended" transcript (the reported bug). The
  // runner keeps running independent of the browser, so when the server still reports the session running/
  // creating we RESUME the SAME session — device-agent-api reconnects the live session and re-mints a token
  // WITHOUT booting a new runner (active._id === sid) — and flip to the live stream. We do NOT use start():
  // opening a NEW session on a device that already has a live one is refused (409). One attempt per focused
  // session; on failure we fall back to the read-only history.
  const { sessions, loading: sessionsLoading } = useDeviceTroubleshootSessions();
  const { resume } = useResumeDeviceTroubleshootSession();
  const reconnectTried = useRef<Set<string>>(new Set());
  const [reconnecting, setReconnecting] = useState(false);

  const focusedId = focused?.session.sessionId ?? null;
  const row = focusedId ? sessions.find((s) => s.sessionId === focusedId) : undefined;
  const isLiveStatus = row?.status === 'running' || row?.status === 'creating';
  const shouldReattach =
    !!focused &&
    focused.mode === 'history' &&
    canResume &&
    isLiveStatus &&
    !!row?.deviceUUID &&
    !!focusedId &&
    !reconnectTried.current.has(focusedId);

  useEffect(() => {
    if (!shouldReattach || !focused || !row?.deviceUUID) return;
    reconnectTried.current.add(focused.session.sessionId);
    setReconnecting(true);
    void (async () => {
      try {
        const handle = await resume(focused.session.sessionId, row.deviceUUID);
        if (handle) {
          openSession({ session: handle, mode: 'live', deviceLabel: focused.deviceLabel });
        }
      } catch {
        // leave the read-only history transcript up (rare)
      } finally {
        setReconnecting(false);
      }
    })();
  }, [shouldReattach, focused, row?.deviceUUID, resume, openSession]);

  // Hooks must run unconditionally — load the transcript only for history mode (skips when id is null).
  const historyId = focused?.mode === 'history' ? focused.session.sessionId : null;
  const { events, loading, error } = useDeviceTroubleshootTranscript(historyId);

  if (!focused) return null;
  const isHistory = focused.mode === 'history';
  // The view seeds its event log from initialEvents ONCE on mount — so for history we must wait for the
  // transcript to finish loading before mounting it, or the view stays empty ("Connecting to the device…").
  const waitingForTranscript = isHistory && loading && events.length === 0;
  // While (re)attaching a still-live session — OR while the fresh status list is still loading (network-only,
  // no cache) so we don't yet know whether it's live — show a "Reconnecting…" state, never a stale Ended flash.
  const showReconnecting =
    reconnecting || shouldReattach || (focused.mode === 'history' && canResume && sessionsLoading);

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="flex shrink-0 items-center border-b border-border px-2 py-2">
        <Button
          size="sm"
          variant="ghost"
          onClick={closeSession}
          aria-label={t('optibot.session.backToChat')}
        >
          <ArrowLeft className="mr-1 h-4 w-4" />
          {t('optibot.session.backToChat')}
        </Button>
      </div>
      <div className="min-h-0 flex-1 overflow-hidden p-2">
        {showReconnecting ? (
          <div className="flex h-full items-center justify-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            {t('optibot.session.conn.reconnecting')}
          </div>
        ) : waitingForTranscript ? (
          <div className="flex h-full items-center justify-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            {t('optibot.session.history.loading')}
          </div>
        ) : isHistory && error ? (
          <div className="p-4 text-xs text-destructive">
            {t('optibot.session.history.loadError')}
          </div>
        ) : (
          <DeviceTroubleshootSessionView
            session={focused.session}
            variant="fullpage"
            mode={focused.mode}
            deviceLabel={focused.deviceLabel}
            {...(isHistory ? { initialEvents: events } : {})}
          />
        )}
      </div>
    </div>
  );
}
